/**
 * Leads L1 (embudo comercial). Contrato: argenlotapi/docs/frontend/FRONTEND_LEADS.md.
 *
 * `ownerUserId` (espacio ajeno, editor de Espacio): GET → `?owner_user_id=`,
 * POST/PATCH → `owner_user_id` en el body. Sin owner → leads propios.
 * `DELETE /deletelead` es solo del dueño: nunca lleva owner.
 *
 * Todas devuelven el envelope de `httpClient` (`_httpOk` / `_httpStatus`).
 * Un fallo de red o timeout vuelve como `{ _httpOk: false, _httpStatus: null }`;
 * el `AbortError` del consumidor se propaga.
 */
import { del, get, patch, post } from './httpClient';
import { spaceOwnerQueryParams } from './spaceMembersService';
import { emitCommercialChanged } from '../Leads/commercialSummary';

export const LEADS_FORBIDDEN_MESSAGE = 'No tenés acceso a los leads de este espacio.';
export const LEAD_NOT_FOUND_MESSAGE = 'Este lead ya no existe. Actualizamos la lista.';
export const LEADS_NETWORK_MESSAGE = 'No se pudo hablar con el servidor.';
export const INQUIRY_NOT_FOUND_MESSAGE = 'Esta consulta ya no existe.';
/** 400 `ITEM_REF_INVALID` (ítem ajeno o inexistente). La API no dice cuál de los dos. */
export const ITEM_REF_INVALID_LIST_MESSAGE = 'Esa lista ya no existe o no es de este espacio.';
export const ITEM_REF_INVALID_LOT_MESSAGE = 'Ese lote ya no existe o no es de este espacio.';
export const ITEM_REF_INVALID_UNIT_MESSAGE = 'Esa unidad ya no existe o no es de este espacio.';
/** V3-0: 400 `MANUAL_ITEMS_DISABLED` (intereses a mano deshabilitados). */
export const MANUAL_ITEMS_DISABLED_MESSAGE = 'Los intereses se generan automáticamente con cada consulta.';
/** V3-5: 400 `STAGE_DEPRECATED` (etapa del lead ya no se escribe; usar etapa del interés). */
export const STAGE_DEPRECATED_MESSAGE = 'La etapa del lead ya no se edita acá: cambiá la etapa del interés.';

/** Filtro de origen del tablero (L2). */
export const LEAD_SOURCES = ['inquiry', 'manual', 'other'];

/** F1: `getleads?via=` — leads con al menos una consulta de ese tipo de colección. */
// `manual` (M2): leads con al menos una consulta cargada a mano.
export const LEAD_VIAS = ['list', 'showroom', 'lot_share', 'manual'];
/** Filtros avanzados (SQL 092). `none` = sin canal cargado. */
export const LEAD_FILTER_CHANNELS = ['share_form', 'whatsapp', 'phone', 'in_person', 'social', 'portal', 'other', 'none'];
export const LEAD_INTEREST_KINDS = ['user_lot', 'cadastral_lot', 'cad_parcel', 'list', 'showroom'];
/** R1: tipos de registro de `getrecordleads` / filtro `item_kind` + `id`. */
export const RECORD_ITEM_KINDS = ['user_lot', 'cadastral_lot', 'cad_parcel'];
export const LEAD_SORTS = ['updated_desc', 'created_desc', 'created_asc', 'last_inquiry_desc', 'last_touch_desc', 'name_asc'];
export const LEAD_TEMPERATURES = ['hot', 'warm', 'cold'];
const LEAD_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function csvOf(values, allowed) {
  const list = (Array.isArray(values) ? values : [])
    .map((v) => trimId(v).toLowerCase())
    .filter((v, i, arr) => allowed.includes(v) && arr.indexOf(v) === i);
  return list.join(',');
}

function dateOf(value) {
  const v = trimId(value);
  return LEAD_DATE_RE.test(v) ? v : '';
}

/** F2: tope de colecciones por pedido de `getleadcounts`. */
export const LEAD_COUNTS_MAX_IDS = 50;

/** 404 `COLLECTION_NOT_FOUND` en `getleads?list_id=` (la lista ya no es de este espacio). */
export const LEADS_COLLECTION_GONE_MESSAGE = 'Esa lista ya no está en este espacio. Mostramos todos los leads.';
/** `getleads` con `list_id` pero sin `list_stats` (API anterior a F1). */
export const LEADS_COLLECTION_UNSUPPORTED_MESSAGE = 'Todavía no se pueden ver los leads por lista. Mostramos todos.';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const NETWORK_ERROR = Object.freeze({ _httpOk: false, _httpStatus: null, error: LEADS_NETWORK_MESSAGE });

async function safeRequest(run) {
  try {
    return await run();
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    return { ...NETWORK_ERROR };
  }
}

function trimId(value) {
  return value == null ? '' : String(value).trim();
}

function missing(message) {
  return { _httpOk: false, _httpStatus: 400, error: message };
}

/**
 * `code` estable de un error de la API (M0). Respuestas viejas sin `code` → ''.
 * Decidir siempre por esto, nunca por el texto de `error`.
 *
 * @param {object} res Envelope.
 * @returns {string}
 */
export function readApiErrorCode(res) {
  const code = res?.code ?? res?.data?.code;
  return typeof code === 'string' ? code.trim().toUpperCase() : '';
}

// ── Filtros por datos del cliente (`pf_*` de getleads) ────────────────────
// Contrato: FRONTEND_LEAD_PROFILE.md § Filtros por perfil del cliente (aprobado
// 20 oct 2026). AND entre filtros, OR dentro de cada uno; `none` = sin dato.
// La API vieja **ignora** los `pf_*` (desplegar API antes que el Front).
export const LEAD_PF_EMPLOYMENT = ['employee', 'self_employed', 'business_owner', 'retired', 'unemployed', 'student', 'other', 'none'];
export const LEAD_PF_CONTACT_TIMES = ['morning', 'afternoon', 'evening', 'none'];
export const LEAD_PF_CONTACT_CHANNELS = ['whatsapp', 'phone', 'email', 'none'];
export const LEAD_PF_HOUSING = ['first_home', 'owner', 'none'];
export const LEAD_PF_ZONE_MAX = 20;
export const LEAD_PF_AGE_MIN = 0;
export const LEAD_PF_AGE_MAX = 120;
export const LEAD_PF_ZONE_MAX_MESSAGE = `Hasta ${LEAD_PF_ZONE_MAX} zonas.`;
const PF_ZONE_FORBIDDEN = /[;|]/;

/**
 * Valor de `pf_zone` para una zona del filtro (item de `/searchplacenames`,
 * `{ kind: 'other' }` o `{ kind: 'none' }`). Formato cerrado con la API
 * (20 oct 2026), partes **tal cual** vienen del catálogo (comparación exacta,
 * sin normalizar tildes ni mayúsculas):
 *   province|<province>
 *   municipality|<province>|<municipality>        (incluye sus barrios)
 *   neighborhood|<province>|<name>|<municipality>
 *   neighborhood|<province>|<name>                (solo si `municipality: null`)
 *   other · none
 * El catálogo no tiene `;` ni `|`; si apareciera, no se manda (`''`).
 */
export function pfZoneToken(zone) {
  const kind = String(zone?.kind ?? '');
  if (kind === 'other' || kind === 'none') return kind;
  const part = (v) => (typeof v === 'string' && v.trim() ? v : '');
  const name = part(zone?.name);
  const province = part(zone?.province) || (kind === 'province' ? name : '');
  const municipality = part(zone?.municipality) || (kind === 'municipality' ? name : '');
  let parts;
  if (kind === 'province' && province) parts = ['province', province];
  else if (kind === 'municipality' && province && municipality) parts = ['municipality', province, municipality];
  else if (kind === 'neighborhood' && province && name) {
    parts = ['neighborhood', province, name, ...(municipality ? [municipality] : [])];
  } else return '';
  return parts.slice(1).some((p) => PF_ZONE_FORBIDDEN.test(p)) ? '' : parts.join('|');
}

/**
 * Arma `pf_zone` (`;` entre tokens). Repetidos se ignoran; zonas que no se
 * pueden expresar se descartan. Más de 20 → `error` (la API daría 400).
 *
 * @param {object[]} zones
 * @returns {{ value: string, error: string }}
 */
export function serializePfZone(zones) {
  const tokens = [...new Set((Array.isArray(zones) ? zones : []).map(pfZoneToken).filter(Boolean))];
  if (tokens.length > LEAD_PF_ZONE_MAX) return { value: '', error: LEAD_PF_ZONE_MAX_MESSAGE };
  return { value: tokens.join(';'), error: '' };
}

const pfAge = (v) => {
  if (v === '' || v == null) return null;
  const n = Number(v);
  return Number.isInteger(n) && n >= LEAD_PF_AGE_MIN && n <= LEAD_PF_AGE_MAX ? n : null;
};

/**
 * Params `pf_*` de `getleads` desde `query.profile`
 * (`{ employment, contactTimes, contactChannels, housing, zones, ageMin, ageMax }`).
 * Valores fuera de la lista blanca no se mandan; edad min > max no se manda.
 *
 * @returns {{ params: Record<string, string|number>, error: string, field: string }}
 */
export function buildLeadProfileFilterParams(profile) {
  const p = profile && typeof profile === 'object' ? profile : {};
  const params = {
    pf_employment_status: csvOf(p.employment, LEAD_PF_EMPLOYMENT),
    pf_contact_time: csvOf(p.contactTimes, LEAD_PF_CONTACT_TIMES),
    pf_contact_channel: csvOf(p.contactChannels, LEAD_PF_CONTACT_CHANNELS),
    pf_housing_status: csvOf(p.housing, LEAD_PF_HOUSING),
  };
  const zone = serializePfZone(p.zones);
  if (zone.error) return { params: {}, error: zone.error, field: 'pf_zone' };
  params.pf_zone = zone.value;
  const min = pfAge(p.ageMin);
  const max = pfAge(p.ageMax);
  if (!(min != null && max != null && min > max)) {
    if (min != null) params.pf_age_min = min;
    if (max != null) params.pf_age_max = max;
  }
  Object.keys(params).forEach((key) => { if (params[key] === '' || params[key] == null) delete params[key]; });
  return { params, error: '', field: '' };
}

/** ¿El error de getleads es de un filtro `pf_*`? → nombre del param, o `''`. */
export function readLeadProfileFilterErrorField(res) {
  if (res?._httpStatus !== 400 || readApiErrorCode(res) !== 'VALIDATION_ERROR') return '';
  const field = trimId(res?.field ?? res?.data?.field);
  return field.startsWith('pf_') ? field : '';
}

/**
 * GET /getleads. Sin `stage` trae todo (≤200) + `stats` del owner.
 * L2: `source` (`inquiry|manual|other`) filtra el origen; `stats` sigue
 * siendo de todo el owner.
 * F1: `listId` **o** `projectId` ("Leads y consultas" (contrato; la UI abre el modal), nunca los dos: no se
 * llama) → la respuesta suma `list_stats`; `via` (`list|showroom|lot_share`).
 *
 * Filtros avanzados (opcionales; día AR `YYYY-MM-DD`): `createdFrom/To`,
 * `updatedFrom/To`, `channels[]` (incl. `none`), `interestKinds[]`,
 * `listScope` (`records` default de la API · `direct`), `sort`. La respuesta
 * suma `total_filtered` y `sort` (ver `readLeadsList`); `stats` sigue global.
 *
 * @param {{ stage?: string, temperature?: string|string[], source?: string, q?: string, limit?: number, offset?: number,
 *   unseen?: boolean|string|number, inactiveDays?: number, interestStages?: string|string[],
 *   listId?: string, projectId?: string, via?: string, createdFrom?: string, createdTo?: string,
 *   updatedFrom?: string, updatedTo?: string, channels?: string[], interestKinds?: string[],
 *   listScope?: string, sort?: string, itemKind?: string, recordId?: string,
 *   profile?: object }} [query]
 * `profile` → params `pf_*` (ver `buildLeadProfileFilterParams`).
 * `itemKind` + `recordId` (registro elegido en el buscador) → `item_kind` + `id`.
 * `temperature` (`hot`/`warm`/`cold`, CSV o array) filtra por temperatura V3.
 * @param {{ ownerUserId?: string|null, signal?: AbortSignal }} [options]
 */
export function getLeads(query = {}, { ownerUserId, signal } = {}) {
  const stage = trimId(query.stage);
  const temperature = csvOf(
    Array.isArray(query.temperature)
      ? query.temperature
      : String(query.temperature || '').split(','),
    LEAD_TEMPERATURES,
  );
  const sourceRaw = trimId(query.source).toLowerCase();
  const source = LEAD_SOURCES.includes(sourceRaw) ? sourceRaw : '';
  const listId = trimId(query.listId);
  const projectId = trimId(query.projectId);
  const viaRaw = trimId(query.via).toLowerCase();
  const via = LEAD_VIAS.includes(viaRaw) ? viaRaw : '';
  // Chips de seguimiento (API DEV 42c97fb): unseen / inactive_days / interest_stage.
  const unseenRaw = query.unseen;
  const unseenOn = unseenRaw === true || unseenRaw === 1 || unseenRaw === '1'
    || ['true', 'yes'].includes(String(unseenRaw || '').toLowerCase());
  const inactiveRaw = Number(query.inactiveDays);
  const inactiveDays = Number.isFinite(inactiveRaw)
    && inactiveRaw >= 1 && inactiveRaw <= 365
    ? Math.trunc(inactiveRaw)
    : 0;
  const interestStage = csvOf(
    Array.isArray(query.interestStages)
      ? query.interestStages
      : String(query.interestStages || '').split(','),
    LEAD_INTEREST_STAGE_KEYS,
  );
  // Registro (buscador de registros): `item_kind` + `id`, siempre juntos.
  const recordKind = trimId(query.itemKind);
  const recordId = trimId(query.recordId);
  const record = RECORD_ITEM_KINDS.includes(recordKind) && recordId ? { item_kind: recordKind, id: recordId } : {};
  if (listId && projectId) return Promise.resolve(missing('Elegí una lista.'));
  const q = trimId(query.q).slice(0, 120);
  const limitRaw = Number(query.limit);
  const limit = Number.isFinite(limitRaw) ? Math.min(200, Math.max(1, Math.trunc(limitRaw))) : undefined;
  const offsetRaw = Number(query.offset);
  const offset = Number.isFinite(offsetRaw) && offsetRaw > 0 ? Math.trunc(offsetRaw) : undefined;
  const advanced = {
    created_from: dateOf(query.createdFrom),
    created_to: dateOf(query.createdTo),
    updated_from: dateOf(query.updatedFrom),
    updated_to: dateOf(query.updatedTo),
    channel: csvOf(query.channels, LEAD_FILTER_CHANNELS),
    interest_kind: csvOf(query.interestKinds, LEAD_INTEREST_KINDS),
    list_scope: listId && query.listScope === 'direct' ? 'direct' : '',
    sort: LEAD_SORTS.includes(query.sort) && query.sort !== 'updated_desc' ? query.sort : '',
  };
  Object.keys(advanced).forEach((key) => { if (!advanced[key]) delete advanced[key]; });
  // Filtros por datos del cliente (`pf_*`). Más de 20 zonas → 400 local (como la API).
  const profileFilters = buildLeadProfileFilterParams(query.profile);
  if (profileFilters.error) {
    return Promise.resolve({
      _httpOk: false, _httpStatus: 400, code: 'VALIDATION_ERROR', field: profileFilters.field, error: profileFilters.error,
    });
  }
  const hasProfileFilters = Object.keys(profileFilters.params).length > 0;
  return safeRequest(async () => {
    const res = await get('/getleads', {
    params: {
      ...(stage ? { stage } : {}),
      ...(temperature ? { temperature } : {}),
      ...(source ? { source } : {}),
      ...(listId ? { list_id: listId } : {}),
      ...(projectId ? { project_id: projectId } : {}),
      ...(via ? { via } : {}),
      ...(unseenOn ? { unseen: '1' } : {}),
      ...(inactiveDays ? { inactive_days: inactiveDays } : {}),
      ...(interestStage ? { interest_stage: interestStage } : {}),
      ...record,
      ...(q ? { q } : {}),
      ...(limit ? { limit } : {}),
      ...(offset ? { offset } : {}),
      ...advanced,
      ...profileFilters.params,
      ...spaceOwnerQueryParams(ownerUserId),
    },
    signal,
    });
    // 503 con `pf_*`: falta la SQL 101 → los filtros de perfil se apagan en la sesión.
    if (hasProfileFilters && isLeadProfileNotEnabled(res)) markLeadProfileDisabled();
    return res;
  });
}

// ── Datos del cliente (`profile`) ─────────────────────────────────────────
// Contrato: argenlotapi/docs/frontend/FRONTEND_LEAD_PROFILE.md (aprobado 20 oct 2026).
// `getlead` y las respuestas de create / update traen siempre `profile`
// (todo `null` sin datos; `profile: null` si falta la SQL 101). Escribir:
// `updatelead` con `profile` **parcial** (clave ausente = no cambia; `null`
// limpia; los arrays se reemplazan enteros). Nunca `profile: null` (400).

export const LEAD_PROFILE_GENDERS = ['female', 'male', 'non_binary', 'other', 'prefer_not_to_say'];
export const LEAD_PROFILE_MARITAL_STATUSES = ['single', 'married', 'civil_union', 'separated', 'divorced', 'widowed', 'other'];
export const LEAD_PROFILE_EMPLOYMENT_STATUSES = ['employee', 'self_employed', 'business_owner', 'retired', 'unemployed', 'student', 'other'];
export const LEAD_PROFILE_CONTACT_CHANNELS = ['whatsapp', 'phone', 'email'];
export const LEAD_PROFILE_CONTACT_TIMES = ['morning', 'afternoon', 'evening'];
export const LEAD_PROFILE_HOUSING_STATUSES = ['first_home', 'owner'];
/** `kind` de `residence_location`: los del catálogo + `other` (texto libre). */
export const LEAD_PROFILE_PLACE_KINDS = ['neighborhood', 'municipality', 'province', 'other'];
export const LEAD_PROFILE_NAME_MAX = 50;
export const LEAD_PROFILE_OCCUPATION_MAX = 120;
export const LEAD_PROFILE_PLACE_NAME_MAX = 120;
export const LEAD_PROFILE_CHILDREN_MAX = 20;
export const LEAD_PROFILE_MIN_BIRTH_DATE = '1900-01-01';
export const LEAD_PROFILE_MIN_YEAR = 1900;
export const LEAD_PROFILE_NOT_ENABLED_MESSAGE = 'Los datos del cliente todavía no están habilitados.';

/** Claves editables (en el orden del contrato). `age` y `updated_*` son solo lectura. */
export const LEAD_PROFILE_KEYS = [
  'first_name', 'last_name', 'birth_date', 'gender', 'marital_status', 'children_count',
  'children_birth_years', 'employment_status', 'occupation', 'residence_location',
  'preferred_contact_channels', 'preferred_contact_times', 'housing_status',
];
const PROFILE_ARRAY_KEYS = ['children_birth_years', 'preferred_contact_channels', 'preferred_contact_times'];
const PROFILE_ENUMS = {
  gender: LEAD_PROFILE_GENDERS,
  marital_status: LEAD_PROFILE_MARITAL_STATUSES,
  employment_status: LEAD_PROFILE_EMPLOYMENT_STATUSES,
  housing_status: LEAD_PROFILE_HOUSING_STATUSES,
};
const PROFILE_TZ = 'America/Argentina/Cordoba';

const isPlainObject = (v) => Boolean(v) && typeof v === 'object' && !Array.isArray(v);
const profileText = (v, max) => {
  if (typeof v !== 'string') return null;
  const t = v.trim().replace(/\s+/g, ' ');
  return t ? t.slice(0, max) : null;
};
const profileInt = (v, min, max) => {
  const n = typeof v === 'string' && v.trim() !== '' ? Number(v) : v;
  return Number.isInteger(n) && n >= min && n <= max ? n : null;
};

/** Hoy en Argentina (`YYYY-MM-DD`). */
export function getLeadProfileTodayAR(now = new Date()) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: PROFILE_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(now);
}

/** Edad (años cumplidos) a una fecha `YYYY-MM-DD`, o `null`. */
export function computeLeadProfileAge(birthDate, now = new Date()) {
  if (!LEAD_DATE_RE.test(String(birthDate || ''))) return null;
  const [by, bm, bd] = birthDate.split('-').map(Number);
  const [ty, tm, td] = getLeadProfileTodayAR(now).split('-').map(Number);
  const age = ty - by - (tm < bm || (tm === bm && td < bd) ? 1 : 0);
  return age >= 0 ? age : null;
}

function normalizeProfilePlace(raw) {
  if (!isPlainObject(raw)) return null;
  const kind = trimId(raw.kind).toLowerCase();
  const name = profileText(raw.name, LEAD_PROFILE_PLACE_NAME_MAX);
  if (!LEAD_PROFILE_PLACE_KINDS.includes(kind) || !name) return null;
  if (kind === 'other') {
    return { kind, name, province: null, municipality: null, department: null, label: name };
  }
  return {
    kind,
    name,
    province: profileText(raw.province, 80),
    municipality: profileText(raw.municipality, 120),
    department: profileText(raw.department, 120),
    label: profileText(raw.label, 240) || name,
  };
}

function normalizeEnumList(raw, allowed) {
  const list = Array.isArray(raw) ? raw.map((v) => trimId(v).toLowerCase()) : [];
  // Orden del enum (como la API): la comparación de cambios queda estable.
  return allowed.filter((v) => list.includes(v));
}

/**
 * `profile` de la API → objeto con **todas** las claves (defaults `null`;
 * arrays `[]`). Valores fuera del contrato se descartan (quedan `null`).
 * `age`: el de la API; si no viene, se calcula con la fecha de hoy en AR.
 *
 * @param {unknown} raw
 * @returns {object}
 */
export function normalizeLeadProfile(raw, now = new Date()) {
  const p = isPlainObject(raw) ? raw : {};
  const thisYear = Number(getLeadProfileTodayAR(now).slice(0, 4));
  const birth = LEAD_DATE_RE.test(trimId(p.birth_date)) ? trimId(p.birth_date) : null;
  const apiAge = profileInt(p.age, 0, 200);
  const enumOf = (key) => {
    const v = trimId(p[key]).toLowerCase();
    return PROFILE_ENUMS[key].includes(v) ? v : null;
  };
  const years = (Array.isArray(p.children_birth_years) ? p.children_birth_years : [])
    .map((y) => profileInt(y, LEAD_PROFILE_MIN_YEAR, thisYear))
    .filter((y) => y != null)
    .sort((a, b) => a - b);
  return {
    first_name: profileText(p.first_name, LEAD_PROFILE_NAME_MAX),
    last_name: profileText(p.last_name, LEAD_PROFILE_NAME_MAX),
    birth_date: birth,
    age: birth ? (apiAge ?? computeLeadProfileAge(birth, now)) : null,
    gender: enumOf('gender'),
    marital_status: enumOf('marital_status'),
    children_count: profileInt(p.children_count, 0, LEAD_PROFILE_CHILDREN_MAX),
    children_birth_years: years,
    employment_status: enumOf('employment_status'),
    occupation: profileText(p.occupation, LEAD_PROFILE_OCCUPATION_MAX),
    residence_location: normalizeProfilePlace(p.residence_location),
    preferred_contact_channels: normalizeEnumList(p.preferred_contact_channels, LEAD_PROFILE_CONTACT_CHANNELS),
    preferred_contact_times: normalizeEnumList(p.preferred_contact_times, LEAD_PROFILE_CONTACT_TIMES),
    housing_status: enumOf('housing_status'),
    updated_at: typeof p.updated_at === 'string' ? p.updated_at : null,
    updated_by_user_id: typeof p.updated_by_user_id === 'string' ? p.updated_by_user_id : null,
  };
}

// 503 `LEAD_PROFILE_NOT_ENABLED` (falta la SQL 101): la edición del perfil se
// oculta por el resto de la sesión (mismo patrón que la consulta manual).
let leadProfileDisabled = false;

/** ¿La API dijo (503) que el perfil no está habilitado en esta sesión? */
export function isLeadProfileDisabled() {
  return leadProfileDisabled;
}

/** 503 `LEAD_PROFILE_NOT_ENABLED` (escritura o filtros `pf_*`): apaga el perfil en la sesión. */
export function markLeadProfileDisabled() {
  leadProfileDisabled = true;
}

/** Solo tests. */
export function resetLeadProfileAvailability() {
  leadProfileDisabled = false;
}

/**
 * ¿Se puede ver / editar el perfil de este lead? La API nueva siempre manda
 * `profile` como objeto; `null` (sin SQL 101) o ausente (API vieja) → no.
 */
export function isLeadProfileAvailable(lead) {
  return isPlainObject(lead?.profile) && !leadProfileDisabled;
}

/** ¿El perfil normalizado tiene algún dato cargado? */
export function hasLeadProfileData(profile) {
  const p = profile || {};
  return LEAD_PROFILE_KEYS.some((key) => (Array.isArray(p[key]) ? p[key].length > 0 : p[key] != null));
}

const profileValueKey = (key, v) => {
  if (PROFILE_ARRAY_KEYS.includes(key)) return JSON.stringify(Array.isArray(v) ? v : []);
  if (key === 'residence_location') {
    return v ? JSON.stringify([v.kind, v.name, v.province || null, v.municipality || null]) : 'null';
  }
  return JSON.stringify(v ?? null);
};

/**
 * Diff para `updatelead`: solo las claves que cambiaron entre dos perfiles
 * normalizados. Vacío (`null` / `''` / `[]`) → `null` (limpia). `{}` = nada.
 * Si `children_count` queda en `null` / `0` y había años, manda también
 * `children_birth_years: null` (el contrato lo exige).
 *
 * @returns {object} Parcial (puede ser `{}`).
 */
export function buildLeadProfilePatch(prevProfile, nextProfile) {
  const prev = normalizeLeadProfile(prevProfile);
  const next = normalizeLeadProfile(nextProfile);
  const patchBody = {};
  LEAD_PROFILE_KEYS.forEach((key) => {
    if (profileValueKey(key, prev[key]) === profileValueKey(key, next[key])) return;
    const value = next[key];
    if (Array.isArray(value)) patchBody[key] = value.length ? value : null;
    else if (key === 'residence_location' && value) {
      patchBody[key] = value.kind === 'other'
        ? { kind: 'other', name: value.name }
        : {
          kind: value.kind,
          name: value.name,
          province: value.province,
          municipality: value.municipality,
          department: value.department,
          label: value.label,
        };
    } else patchBody[key] = value ?? null;
  });
  if (Object.prototype.hasOwnProperty.call(patchBody, 'children_count')
    && !next.children_count && prev.children_birth_years.length
    && !Object.prototype.hasOwnProperty.call(patchBody, 'children_birth_years')) {
    patchBody.children_birth_years = null;
  }
  return patchBody;
}

const joinProfileName = (first, last) => [first, last]
  .map((v) => String(v || '').trim()).filter(Boolean).join(' ');
const sameName = (a, b) => String(a || '').trim().replace(/\s+/g, ' ').toLowerCase()
  === String(b || '').trim().replace(/\s+/g, ' ').toLowerCase();

/**
 * Sync de `name` (lo maneja el Front: la API no lo toca en `updatelead`).
 * Si cambiaron nombre / apellido **y** `name` estaba vacío o era igual a la
 * combinación anterior («first last»), devuelve el nuevo «first last». Si no
 * (o si la combinación nueva queda vacía), `null` = no tocar `name`.
 *
 * @param {{ name?: string, prev?: object, next?: object }} args Perfiles (normalizados o crudos).
 * @returns {string|null}
 */
export function syncLeadNameFromProfile({ name, prev, next } = {}) {
  const before = joinProfileName(prev?.first_name, prev?.last_name);
  const after = joinProfileName(next?.first_name, next?.last_name);
  if (!after || sameName(before, after)) return null;
  if (String(name || '').trim() && !sameName(name, before)) return null;
  return sameName(name, after) ? null : after;
}

/**
 * Sugerencia para leads sin nombre / apellido: parte `name` en el último
 * espacio (contrato: solo se guarda si el corredor confirma).
 * @returns {{ first_name: string, last_name: string }|null}
 */
export function suggestLeadProfileNames(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return null;
  return {
    first_name: parts.slice(0, -1).join(' ').slice(0, LEAD_PROFILE_NAME_MAX),
    last_name: parts[parts.length - 1].slice(0, LEAD_PROFILE_NAME_MAX),
  };
}

/**
 * 400 `VALIDATION_ERROR` con `field: "profile.<campo>"` → `<campo>` (para
 * marcarlo en el form). `field: "profile"` → `'profile'`. Otro error → `''`.
 */
export function readLeadProfileErrorField(res) {
  if (res?._httpStatus !== 400 || readApiErrorCode(res) !== 'VALIDATION_ERROR') return '';
  const field = trimId(res?.field ?? res?.data?.field);
  if (field === 'profile') return 'profile';
  const m = /^profile\.([a-z_]+)/.exec(field);
  return m ? m[1] : '';
}

/** ¿503 `LEAD_PROFILE_NOT_ENABLED`? */
export function isLeadProfileNotEnabled(res) {
  return res?._httpStatus === 503 && readApiErrorCode(res) === 'LEAD_PROFILE_NOT_ENABLED';
}

/** `profile` para mandar: objeto sin claves de solo lectura ni desconocidas; `null` si queda vacío. */
function cleanProfileForWrite(raw, { dropNulls = false } = {}) {
  if (!isPlainObject(raw)) return null;
  const out = {};
  LEAD_PROFILE_KEYS.forEach((key) => {
    if (!Object.prototype.hasOwnProperty.call(raw, key)) return;
    const v = raw[key];
    if (dropNulls && (v == null || v === '' || (Array.isArray(v) && !v.length))) return;
    out[key] = v;
  });
  return Object.keys(out).length ? out : null;
}

// ── Buscador de lugares (`GET /searchplacenames`) ────────────────────────
// Recuperado de 59042a9 (se usaba para `interest_filters`). Hoy sirve para
// `profile.residence_location`. Sin `owner_user_id` (catálogo único).
export const PLACE_NAME_KINDS = ['neighborhood', 'municipality', 'province'];
/**
 * Provincias del catálogo hasta que llegue la primera `coverage` (nombres
 * tal cual los devuelve la API: `schemas/nl_place_catalog.json`, 19 oct 2026).
 */
export const PLACE_NAME_PROVINCES = [
  'Buenos Aires', 'Catamarca', 'Chaco', 'Chubut', 'Ciudad Autonoma de Buenos Aires', 'Córdoba',
  'Corrientes', 'Entre Ríos', 'Formosa', 'Jujuy', 'La Pampa', 'La Rioja', 'Mendoza', 'Misiones',
  'Neuquén', 'Río Negro', 'Salta', 'San Juan', 'San Luis', 'Santa Cruz', 'Santa Fe',
  'Santiago del Estero', 'Tierra del Fuego', 'Tucumán',
];

// `coverage` de la última respuesta: el catálogo puede cambiar sin tocar el Front.
let placeCoverage = PLACE_NAME_PROVINCES;
const coverageListeners = new Set();

/** Provincias del catálogo (la última `coverage` recibida, o la default). */
export function getPlaceNameCoverage() {
  return placeCoverage;
}

/** Suscribirse a cambios de `coverage` (para `useSyncExternalStore`). */
export function subscribePlaceNameCoverage(listener) {
  coverageListeners.add(listener);
  return () => coverageListeners.delete(listener);
}

function rememberPlaceCoverage(res) {
  const list = Array.isArray(res?.coverage)
    ? [...new Set(res.coverage.map((p) => String(p || '').trim()).filter(Boolean))]
    : [];
  if (!list.length || JSON.stringify(list) === JSON.stringify(placeCoverage)) return;
  placeCoverage = list;
  coverageListeners.forEach((fn) => fn());
}

/** Solo para tests. */
export function resetPlaceNameCoverage() {
  placeCoverage = PLACE_NAME_PROVINCES;
}
export const PLACE_NAME_MIN_CHARS = 2;
const PLACE_NAME_MAX_CHARS = 80;

/**
 * GET /searchplacenames (JWT). `q` < 2 caracteres → `{ _httpOk: true, data: [] }`
 * sin red. `kind` fuera de la whitelist no se manda (`department` → 400 en la API).
 *
 * @param {{ q: string, kind?: string|string[], province?: string, limit?: number }} args
 * @param {{ signal?: AbortSignal }} [opts]
 */
export function searchPlaceNames({ q, kind, province, limit } = {}, { signal } = {}) {
  const query = String(q || '').trim().slice(0, PLACE_NAME_MAX_CHARS);
  const kinds = (Array.isArray(kind) ? kind : String(kind || '').split(','))
    .map((k) => String(k).trim().toLowerCase())
    .filter(Boolean);
  const allowedKinds = kinds.filter((k, i) => PLACE_NAME_KINDS.includes(k) && kinds.indexOf(k) === i);
  if (query.length < PLACE_NAME_MIN_CHARS || (kinds.length && !allowedKinds.length)) {
    return Promise.resolve({ _httpOk: true, _httpStatus: 200, data: [] });
  }
  const params = { q: query };
  if (allowedKinds.length) params.kind = allowedKinds.join(',');
  const prov = String(province || '').trim();
  if (prov && prov.length <= 80) params.province = prov;
  const n = Math.trunc(Number(limit));
  if (Number.isFinite(n) && n >= 1) params.limit = Math.min(50, n);
  return safeRequest(async () => {
    const res = await get('/searchplacenames', { params, signal });
    if (res?._httpOk) rememberPlaceCoverage(res);
    return res;
  });
}

/**
 * Filas válidas de `/searchplacenames` (`data`, no `items`), con el item
 * entero (es lo que guarda `residence_location`).
 * @returns {{ kind: string, name: string, label: string, province: string|null,
 *   municipality: string|null, department: string|null }[]}
 */
export function readPlaceNames(res) {
  const rows = Array.isArray(res?.data) ? res.data : [];
  return rows
    .filter((r) => r && PLACE_NAME_KINDS.includes(r.kind) && typeof r.name === 'string' && r.name.trim())
    .map((r) => ({
      kind: r.kind,
      name: r.name,
      label: typeof r.label === 'string' && r.label.trim() ? r.label : r.name,
      province: r.province || null,
      municipality: r.municipality || null,
      department: r.department || null,
    }));
}

/** Texto para un error de `/searchplacenames` (400 trae mensaje en español). */
export function getPlaceNamesErrorMessage(res) {
  if (res?._httpStatus === 401) return 'Iniciá sesión para buscar lugares.';
  const apiText = typeof res?.error === 'string' ? res.error.trim() : '';
  if (res?._httpStatus === 400 && apiText) return apiText;
  if (res?._httpStatus == null) return LEADS_NETWORK_MESSAGE;
  return 'No se pudo buscar el lugar.';
}

/**
 * GET /getlead?id= — detalle con `items` y `activity`. Ajeno → 404.
 *
 * @param {string} id
 * @param {{ ownerUserId?: string|null, signal?: AbortSignal }} [options]
 */
export function getLead(id, { ownerUserId, signal } = {}) {
  const leadId = trimId(id);
  if (!leadId) return Promise.resolve(missing('Falta el id del lead.'));
  return safeRequest(() => get('/getlead', {
    params: { id: leadId, ...spaceOwnerQueryParams(ownerUserId) },
    signal,
  }));
}

/**
 * POST /createlead. 409 `LEAD_DUPLICATE` + `existing_lead_id` (ver
 * `readLeadDuplicateId`). No hay `force` en el contrato.
 * V3-0: nunca manda `items` (los intereses nacen de una consulta del form).
 * V3-5: nunca manda `stage` / `lost_reason` (la API siempre arranca en `new`).
 *
 * @param {{ name: string, email?: string, phone?: string, interest?: string,
 *   source?: string, channel?: string }} body
 * @param {{ ownerUserId?: string|null, signal?: AbortSignal }} [options]
 */
export function createLead(body = {}, { ownerUserId, signal } = {}) {
  const payload = {};
  ['name', 'email', 'phone', 'interest'].forEach((key) => {
    const value = typeof body[key] === 'string' ? body[key].trim() : '';
    if (value) payload[key] = value;
  });
  const source = trimId(body.source);
  if (source) payload.source = source;
  // `channel` opcional: sin valor no se manda (la API lo deja en null).
  const channel = trimId(body.channel);
  if (channel) payload.channel = channel;
  // `profile` opcional (contrato de perfil): solo claves con valor. Con
  // nombre / apellido y sin `name`, la API arma `name`. El alta del Front no lo usa.
  const profile = cleanProfileForWrite(body.profile, { dropNulls: true });
  if (profile) payload.profile = profile;
  if (!payload.name && !profile?.first_name && !profile?.last_name) {
    return Promise.resolve(missing('Cargá el nombre.'));
  }
  return safeRequest(async () => {
    const res = await post('/createlead', {
      body: { ...payload, ...spaceOwnerQueryParams(ownerUserId) },
      signal,
    });
    if (isLeadProfileNotEnabled(res)) leadProfileDisabled = true;
    return res;
  });
}

// `interest_filters` (preferencias declaradas v1) se dio de baja el 20 oct 2026:
// no se manda en createlead / updatelead (aunque venga en el body) ni hay `if_*`
// en getleads. Tras el deploy de la API, esas respuestas pueden traer
// `warnings: [{ code: 'INTEREST_FILTERS_DEPRECATED', … }]`: se ignora. `getlead`
// todavía devuelve `interest_filters` (solo lectura) y tampoco se usa.
// `channel: null` lo limpia. V3-0: sin `add_items` / `remove_item_ids`.
// V3-5: sin `stage` / `lost_reason` (400 `STAGE_DEPRECATED`; etapa vía /updateleadinterest).
const UPDATABLE_KEYS = ['name', 'email', 'phone', 'interest', 'channel'];

/**
 * PATCH /updatelead. Solo se mandan las claves presentes (datos / canal /
 * `profile` parcial). 503 `LEAD_PROFILE_NOT_ENABLED` → `isLeadProfileDisabled()`.
 * La etapa del lead ya no se escribe acá (V3-5).
 *
 * @param {{ id: string } & Record<string, unknown>} body
 * @param {{ ownerUserId?: string|null, signal?: AbortSignal }} [options]
 */
export function updateLead(body = {}, { ownerUserId, signal } = {}) {
  const id = trimId(body.id);
  if (!id) return Promise.resolve(missing('Falta el id del lead.'));
  const payload = { id };
  UPDATABLE_KEYS.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(body, key)) payload[key] = body[key];
  });
  // `profile` parcial (ver `buildLeadProfilePatch`). Nunca `profile: null` ni `{}`.
  const profile = cleanProfileForWrite(body.profile);
  if (profile) payload.profile = profile;
  if (Object.keys(payload).length === 1) return Promise.resolve(missing('Nada para actualizar.'));
  return safeRequest(async () => {
    const res = await patch('/updatelead', {
      body: { ...payload, ...spaceOwnerQueryParams(ownerUserId) },
      signal,
    });
    if (isLeadProfileNotEnabled(res)) leadProfileDisabled = true;
    return res;
  });
}

/** V3-3: etapas por interés (claves ES). */
export const LEAD_INTEREST_STAGE_KEYS = [
  'nuevo', 'contactado', 'en_seguimiento', 'ofertado', 'reservado', 'cerrado', 'descartado',
];
export const LEAD_OFFER_CURRENCIES = ['ARS', 'USD'];
/** 400 `ITEM_NOT_TRACKABLE` (interés de lista / showroom: sin etapa). */
export const ITEM_NOT_TRACKABLE_MESSAGE = 'Este interés no tiene etapa (es una lista o un showroom).';
/** 404 `ITEM_NOT_FOUND`. */
export const LEAD_ITEM_NOT_FOUND_MESSAGE = 'Este interés ya no existe. Actualizamos el lead.';

/**
 * PATCH /updateleadinterest (V3-3). Cambia la etapa de un interés.
 * `apply_stock` va **siempre** explícito (la API usa `true` por defecto; el
 * Front nunca toca el stock sin que el usuario lo marque).
 * `offerAmount: null` borra el monto; sin la clave no se manda.
 *
 * @param {{ leadId: string, itemId: string, stage: string, offerAmount?: number|null,
 *   offerCurrency?: string, discardReason?: string, note?: string, applyStock?: boolean }} args
 * @param {{ ownerUserId?: string|null, signal?: AbortSignal }} [options]
 */
export function updateLeadInterest(args = {}, { ownerUserId, signal } = {}) {
  const lead_id = trimId(args.leadId);
  const item_id = trimId(args.itemId);
  const stage = trimId(args.stage).toLowerCase();
  if (!lead_id) return Promise.resolve(missing('Falta el id del lead.'));
  if (!item_id) return Promise.resolve(missing('Falta el interés.'));
  if (!LEAD_INTEREST_STAGE_KEYS.includes(stage)) return Promise.resolve(missing('Elegí una etapa válida.'));
  const body = { lead_id, item_id, stage, apply_stock: args.applyStock === true };
  if (Object.prototype.hasOwnProperty.call(args, 'offerAmount')) {
    const raw = args.offerAmount;
    if (raw === null || raw === '') {
      body.offer_amount = null;
    } else {
      const amount = Number(raw);
      if (!Number.isFinite(amount) || amount < 0) return Promise.resolve(missing('Ingresá un monto válido.'));
      const currency = trimId(args.offerCurrency).toUpperCase();
      if (!LEAD_OFFER_CURRENCIES.includes(currency)) return Promise.resolve(missing('Elegí la moneda (ARS o USD).'));
      body.offer_amount = amount;
      body.offer_currency = currency;
    }
  }
  const reason = trimId(args.discardReason);
  if (reason && stage === 'descartado') body.discard_reason = reason;
  const note = typeof args.note === 'string' ? args.note.trim().slice(0, 4000) : '';
  if (note) body.note = note;
  return safeRequest(() => patch('/updateleadinterest', {
    body: { ...body, ...spaceOwnerQueryParams(ownerUserId) },
    signal,
  })).then((res) => {
    // El resumen comercial de Registros / listas se recalcula en la API.
    emitCommercialChanged({ reason: 'interest_stage', leadId: lead_id, itemId: item_id });
    return res;
  });
}

/**
 * Respuesta de `updateLeadInterest` → `{ item, activity, stockUpdate }`
 * (`null` si no vino ítem).
 *
 * @param {object} res
 */
export function readLeadInterestUpdate(res) {
  const item = res?.data && typeof res.data === 'object' && !Array.isArray(res.data) && res.data.id ? res.data : null;
  if (!item) return null;
  const activity = res?.activity && typeof res.activity === 'object' && res.activity.id ? res.activity : null;
  const stockUpdate = res?.stock_update && typeof res.stock_update === 'object' ? res.stock_update : null;
  return { item, activity, stockUpdate };
}

/**
 * POST /addleadnote. 201 → `data` = fila de activity (`note_added`).
 *
 * @param {{ leadId: string, note: string }} args
 * @param {{ ownerUserId?: string|null, signal?: AbortSignal }} [options]
 */
export function addLeadNote({ leadId, note } = {}, { ownerUserId, signal } = {}) {
  const lead_id = trimId(leadId);
  const text = typeof note === 'string' ? note.trim().slice(0, 4000) : '';
  if (!lead_id) return Promise.resolve(missing('Falta el id del lead.'));
  if (!text) return Promise.resolve(missing('Escribí la nota.'));
  return safeRequest(() => post('/addleadnote', {
    body: { lead_id, note: text, ...spaceOwnerQueryParams(ownerUserId) },
    signal,
  }));
}

/**
 * DELETE /deletelead?id=. Solo el dueño (sin owner_user_id). SQL 098:
 * **archiva** (`{id, deleted: true, archived: true, archived_at}`); consultas e
 * historial quedan y `getleads` / `getleadcounts` / `getrecordleads` lo ocultan
 * por defecto (`archived=only|include` existe; el front todavía no lo usa).
 *
 * @param {string} id
 * @param {{ signal?: AbortSignal }} [options]
 */
export function deleteLead(id, { signal } = {}) {
  const leadId = trimId(id);
  if (!leadId) return Promise.resolve(missing('Falta el id del lead.'));
  return safeRequest(() => del('/deletelead', { params: { id: leadId }, signal }));
}

/**
 * POST /markleadinquiriesseen (M3a): marca vistas **todas** las consultas no
 * vistas del lead (también las de otras listas). Idempotente.
 *
 * @param {string} leadId
 * @param {{ ownerUserId?: string|null, signal?: AbortSignal }} [options]
 */
export function markLeadInquiriesSeen(leadId, { ownerUserId, signal } = {}) {
  const lead_id = trimId(leadId);
  if (!lead_id) return Promise.resolve(missing('Falta el id del lead.'));
  return safeRequest(() => post('/markleadinquiriesseen', {
    body: { lead_id, ...spaceOwnerQueryParams(ownerUserId) },
    signal,
  })).then((res) => {
    emitCommercialChanged({ reason: 'inquiries_seen', leadId: lead_id });
    return res;
  });
}

function normalizeCollectionIds(ids) {
  const seen = new Set();
  (Array.isArray(ids) ? ids : []).forEach((raw) => {
    const id = trimId(raw).toLowerCase();
    if (UUID_REGEX.test(id)) seen.add(id);
  });
  return Array.from(seen);
}

/**
 * Ids para `getleadcounts`: en minúscula, sin repetidos, solo uuid (uno
 * inválido haría fallar todo el pedido con 400) y como mucho 50 entre las dos.
 *
 * @param {{ listIds?: string[], projectIds?: string[] }} input
 * @returns {{ listIds: string[], projectIds: string[] }}
 */
export function buildLeadCountsIds({ listIds = [], projectIds = [] } = {}) {
  const lists = normalizeCollectionIds(listIds).slice(0, LEAD_COUNTS_MAX_IDS);
  const projects = normalizeCollectionIds(projectIds).slice(0, LEAD_COUNTS_MAX_IDS - lists.length);
  return { listIds: lists, projectIds: projects };
}

/**
 * GET /getleadcounts (F2): `{ leads, new_leads, unseen_leads }` por lista /
 * lista CAD, en un solo pedido. Badge = `unseen_leads`. Editor solo de lista
 * → 403 `SPACE_FORBIDDEN`; ids ajenos → `ignored_ids`. Sin ids no se llama.
 *
 * @param {{ listIds?: string[], projectIds?: string[] }} ids
 * @param {{ ownerUserId?: string|null, signal?: AbortSignal }} [options]
 */
export function getLeadCounts(ids = {}, { ownerUserId, signal } = {}) {
  const { listIds, projectIds } = buildLeadCountsIds(ids);
  if (!listIds.length && !projectIds.length) return Promise.resolve(missing('Sin listas para contar.'));
  return safeRequest(() => get('/getleadcounts', {
    params: {
      ...(listIds.length ? { list_ids: listIds.join(',') } : {}),
      ...(projectIds.length ? { project_ids: projectIds.join(',') } : {}),
      ...spaceOwnerQueryParams(ownerUserId),
    },
    signal,
  }));
}

function readCountsRow(row) {
  if (!row || typeof row !== 'object') return null;
  const n = (value) => {
    const num = Number(value);
    return Number.isFinite(num) && num > 0 ? Math.trunc(num) : 0;
  };
  return { leads: n(row.leads), newLeads: n(row.new_leads), unseenLeads: n(row.unseen_leads) };
}

function readCountsMap(raw) {
  const map = {};
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return map;
  Object.entries(raw).forEach(([id, row]) => {
    const key = trimId(id).toLowerCase();
    const counts = readCountsRow(row);
    if (key && counts) map[key] = counts;
  });
  return map;
}

/**
 * Envelope OK de `getleadcounts` → contadores por id (keys en minúscula).
 * Un id que no está (ajeno, en `ignored_ids`) = sin leads para este espacio.
 *
 * @param {object} res
 * @returns {{ lists: Record<string, { leads: number, newLeads: number, unseenLeads: number }>,
 *   projects: Record<string, { leads: number, newLeads: number, unseenLeads: number }>,
 *   ignoredIds: string[] }|null}
 */
export function readLeadCounts(res) {
  if (!res?._httpOk) return null;
  const data = res?.data;
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  return {
    lists: readCountsMap(data.lists),
    projects: readCountsMap(data.projects),
    ignoredIds: (Array.isArray(res.ignored_ids) ? res.ignored_ids : [])
      .map((id) => trimId(id).toLowerCase())
      .filter(Boolean),
  };
}

/**
 * `list_stats` de `getleads?list_id=|project_id=` (de toda la colección, no
 * sigue a etapa / búsqueda). Sin el campo → null (API anterior a F1: ignoró
 * el filtro y trajo todos los leads).
 *
 * @param {object} res
 * @returns {{ kind: string, collectionId: string, total: number,
 *   byStage: Record<string, number>, newLeads: number, unseenLeads: number }|null}
 */
export function readLeadListStats(res) {
  const raw = res?.list_stats;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const n = (value) => {
    const num = Number(value);
    return Number.isFinite(num) && num > 0 ? Math.trunc(num) : 0;
  };
  const byStage = raw.by_stage && typeof raw.by_stage === 'object' ? raw.by_stage : {};
  const byTemperature = raw.by_temperature && typeof raw.by_temperature === 'object'
    ? raw.by_temperature
    : undefined;
  return {
    kind: trimId(raw.kind),
    collectionId: trimId(raw.collection_id),
    total: n(raw.total),
    byStage,
    ...(byTemperature ? { byTemperature } : {}),
    newLeads: n(raw.new_leads),
    unseenLeads: n(raw.unseen_leads),
  };
}

/** Bandeja (L2): `can_view_leads` en la raíz de GET /getcollectioninquiries. */
export function readInquiryCanViewLeads(res) {
  return res?.can_view_leads === true;
}

/**
 * @param {object} res Envelope de GET /getleads.
 * @returns {{ leads: object[], stats: object|null, hasMore: boolean, nextOffset: number|null }}
 */
export function readLeadsList(res) {
  return {
    leads: Array.isArray(res?.data) ? res.data : [],
    stats: res?.stats && typeof res.stats === 'object' ? res.stats : null,
    hasMore: res?.has_more === true,
    nextOffset: Number.isFinite(Number(res?.next_offset)) && res?.next_offset != null
      ? Number(res.next_offset)
      : null,
    // API sin filtros avanzados: no viene → `null`.
    totalFiltered: Number.isFinite(Number(res?.total_filtered)) && res?.total_filtered != null
      ? Math.max(0, Math.trunc(Number(res.total_filtered)))
      : null,
  };
}

/** @returns {object|null} Lead completo de getlead / create / update. */
export function readLead(res) {
  const data = res?.data;
  return data && typeof data === 'object' && !Array.isArray(data) && data.id ? data : null;
}

function itemRefInvalidMessage(itemKind) {
  if (itemKind === 'list' || itemKind === 'showroom') return ITEM_REF_INVALID_LIST_MESSAGE;
  if (itemKind === 'user_lot') return ITEM_REF_INVALID_UNIT_MESSAGE;
  return ITEM_REF_INVALID_LOT_MESSAGE;
}

/** 409 LEAD_DUPLICATE → id del lead existente (o '' si no aplica). */
export function readLeadDuplicateId(res) {
  if (res?._httpStatus !== 409) return '';
  if (res?.code && res.code !== 'LEAD_DUPLICATE') return '';
  return trimId(res?.existing_lead_id ?? res?.data?.existing_lead_id);
}

/**
 * Mensaje de UI para un error de Leads.
 *
 * @param {object} res Envelope.
 * @param {string} fallback
 * @returns {string}
 */
export function getLeadErrorMessage(res, fallback = 'No se pudo completar la acción.') {
  const status = res?._httpStatus;
  const apiText = typeof res?.error === 'string' ? res.error.trim() : '';
  // M0: decidir por `code`. Las ramas por texto quedan para respuestas sin `code`.
  const code = readApiErrorCode(res);
  if (status === 401) return 'Iniciá sesión para ver tus leads.';
  if (code === 'LEAD_PROFILE_NOT_ENABLED') return LEAD_PROFILE_NOT_ENABLED_MESSAGE;
  // 500: `INTERNAL_ERROR` (create / update / getlead con perfil) o `SERVER_ERROR`
  // (resto): siempre genérico, sin el detalle técnico.
  if (status >= 500 && ['INTERNAL_ERROR', 'SERVER_ERROR'].includes(code)) return fallback;
  if (status === 400 && code === 'VALIDATION_ERROR' && /^profile(\.|$)/.test(String(res?.field || ''))) {
    return apiText || 'Revisá los datos del cliente.';
  }
  if (status === 400 && code === 'ITEM_REF_INVALID') return itemRefInvalidMessage(res?.item_kind);
  if (status === 400 && code === 'MANUAL_ITEMS_DISABLED') return MANUAL_ITEMS_DISABLED_MESSAGE;
  if (status === 400 && code === 'STAGE_DEPRECATED') return STAGE_DEPRECATED_MESSAGE;
  if (status === 400 && code === 'ITEM_NOT_TRACKABLE') return ITEM_NOT_TRACKABLE_MESSAGE;
  if (code === 'ITEM_NOT_FOUND') return LEAD_ITEM_NOT_FOUND_MESSAGE;
  if (status === 400 && code === 'VALIDATION_ERROR' && ['offer_amount', 'offer_currency'].includes(res?.field)) {
    return apiText ? `Revisá el monto: ${apiText}` : 'Revisá el monto y la moneda.';
  }
  if (status === 400 && code === 'VALIDATION_ERROR' && ['stage', 'discard_reason', 'note', 'apply_stock'].includes(res?.field)) {
    return apiText || 'Revisá los datos de la etapa.';
  }
  if (code === 'LEAD_DELETE_OWNER_ONLY') return apiText || 'Solo el dueño puede archivar leads.';
  if (code === 'SPACE_FORBIDDEN') return LEADS_FORBIDDEN_MESSAGE;
  if (status === 400 && code === 'VALIDATION_ERROR' && /^(created|updated)_(from|to)$/.test(String(res?.field || ''))) {
    return 'Revisá las fechas del filtro.';
  }
  if (status === 400 && code === 'VALIDATION_ERROR' && ['channel', 'interest_kind', 'sort', 'list_scope', 'temperature', 'unseen', 'inactive_days', 'interest_stage', 'source', 'via'].includes(res?.field)) {
    return 'Hay un filtro que no se pudo aplicar. Probá limpiar los filtros.';
  }
  if (code === 'INQUIRY_NOT_FOUND') return INQUIRY_NOT_FOUND_MESSAGE;
  if (code === 'LEAD_NOT_FOUND') return LEAD_NOT_FOUND_MESSAGE;
  if (code === 'COLLECTION_NOT_FOUND') return LEADS_COLLECTION_GONE_MESSAGE;
  if (status === 403) {
    // Borrar como invitado tiene su propio texto en la API.
    return /dueño/i.test(apiText) ? apiText : LEADS_FORBIDDEN_MESSAGE;
  }
  if (status === 404) return /inquiry/i.test(apiText) ? INQUIRY_NOT_FOUND_MESSAGE : LEAD_NOT_FOUND_MESSAGE;
  if (status === 409) return apiText || 'Ya existe un lead con ese email o teléfono.';
  if (status == null) return res?.error || LEADS_NETWORK_MESSAGE;
  return apiText || fallback;
}


/**
 * GET /getrecordleads — consultas y leads de un registro (UF, lote catastral,
 * parcela CAD). `limit` pagina las consultas; `before` + `beforeId` es keyset
 * (igual que `/getinquiries`). `leads` y `stats` vienen siempre completos.
 *
 * @param {{ itemKind: string, id: string, limit?: number, before?: string, beforeId?: string }} query
 * @param {{ ownerUserId?: string|null, signal?: AbortSignal }} [options]
 */
export function getRecordLeads(query = {}, { ownerUserId, signal } = {}) {
  const itemKind = trimId(query.itemKind);
  const id = trimId(query.id);
  if (!RECORD_ITEM_KINDS.includes(itemKind) || !id) {
    return Promise.resolve(missing('Falta el registro.'));
  }
  const limitRaw = Number(query.limit);
  const limit = Number.isFinite(limitRaw) ? Math.min(200, Math.max(1, Math.trunc(limitRaw))) : undefined;
  const before = trimId(query.before);
  const beforeId = trimId(query.beforeId);
  return safeRequest(() => get('/getrecordleads', {
    params: {
      item_kind: itemKind,
      id,
      ...(limit ? { limit } : {}),
      ...(before ? { before } : {}),
      ...(before && beforeId ? { before_id: beforeId } : {}),
      ...spaceOwnerQueryParams(ownerUserId),
    },
    signal,
  }));
}

function readCount(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : 0;
}

/**
 * @param {object} res Envelope de GET /getrecordleads.
 * @returns {{ inquiries: object[], leads: object[],
 *   stats: { inquiryCount: number, unseenCount: number, leadCount: number },
 *   hasMore: boolean, nextBefore: string, nextBeforeId: string }}
 */
export function readRecordLeads(res) {
  const data = res?.data && typeof res.data === 'object' && !Array.isArray(res.data) ? res.data : {};
  const stats = res?.stats && typeof res.stats === 'object' ? res.stats : {};
  return {
    inquiries: Array.isArray(data.inquiries) ? data.inquiries : [],
    leads: Array.isArray(data.leads) ? data.leads.filter((lead) => lead?.id) : [],
    stats: {
      inquiryCount: readCount(stats.inquiry_count),
      unseenCount: readCount(stats.unseen_count),
      leadCount: readCount(stats.lead_count),
    },
    hasMore: res?.has_more === true,
    nextBefore: trimId(res?.next_before),
    nextBeforeId: trimId(res?.next_before_id),
  };
}

/**
 * Errores que ocultan la sección sin avisar: sin sesión, sin acceso a leads
 * del espacio (editor solo de lista) o registro fuera del owner.
 *
 * @param {object} res Envelope.
 * @returns {boolean}
 */
export function isRecordLeadsHidden(res) {
  const status = res?._httpStatus;
  return status === 401 || status === 403 || status === 404;
}

export const LEAD_RECORD_SEARCH_MIN = 2;
export const LEAD_RECORD_SEARCH_MAX = 120;
export const LEAD_RECORD_SEARCH_LIMIT = 20;
export const LEAD_RECORD_SEARCH_UNAVAILABLE_MESSAGE = 'Búsqueda no disponible.';
/**
 * Único lugar para el filtro de actividad: el panel de Leads solo quiere
 * registros con consultas/leads (`has_activity=true`, contrato APIBOT; la API
 * vieja lo ignora).
 */
export const LEAD_RECORD_SEARCH_ACTIVITY_ONLY = true;

/**
 * GET /searchleadrecords — buscador de registros del panel de Leads (SQL 096/097).
 * `q` 2–120 (si no, 400 local sin pedir). `itemKinds` filtra qué registros
 * matchean. `owner_user_id` igual que `/getleads`.
 *
 * `has_activity` sale de `LEAD_RECORD_SEARCH_ACTIVITY_ONLY` (o `query.hasActivity`).
 *
 * `includeLists` (M3) → `include=lists`: cada grupo / unit trae `lists`.
 *
 * @param {{ q: string, itemKinds?: string[], limit?: number, offset?: number, counts?: boolean,
 *   hasActivity?: boolean, includeLists?: boolean }} query
 * @param {{ ownerUserId?: string|null, signal?: AbortSignal }} [options]
 */
export function searchLeadRecords(query = {}, { ownerUserId, signal } = {}) {
  const q = trimId(query.q).slice(0, LEAD_RECORD_SEARCH_MAX);
  if (q.length < LEAD_RECORD_SEARCH_MIN) return Promise.resolve(missing('Escribí al menos 2 caracteres.'));
  const limitRaw = Number(query.limit);
  const limit = Number.isFinite(limitRaw) ? Math.min(50, Math.max(1, Math.trunc(limitRaw))) : undefined;
  const offsetRaw = Number(query.offset);
  const offset = Number.isFinite(offsetRaw) && offsetRaw > 0 ? Math.min(1000, Math.trunc(offsetRaw)) : undefined;
  const itemKind = csvOf(query.itemKinds, RECORD_ITEM_KINDS);
  return safeRequest(() => get('/searchleadrecords', {
    params: {
      q,
      ...(itemKind ? { item_kind: itemKind } : {}),
      ...(limit ? { limit } : {}),
      ...(offset ? { offset } : {}),
      ...(query.counts === false ? { counts: 'false' } : {}),
      has_activity: String(typeof query.hasActivity === 'boolean' ? query.hasActivity : LEAD_RECORD_SEARCH_ACTIVITY_ONLY),
      ...(query.includeLists ? { include: 'lists' } : {}),
      ...spaceOwnerQueryParams(ownerUserId),
    },
    signal,
  }));
}

function readRecordCounts(raw) {
  const c = raw && typeof raw === 'object' ? raw : {};
  return {
    inquiryCount: readCount(c.inquiry_count),
    unseenCount: readCount(c.unseen_count),
    leadCount: readCount(c.lead_count),
    lastInquiryAt: trimId(c.last_inquiry_at),
  };
}

function readRecordHit(raw) {
  const itemKind = trimId(raw?.item_kind);
  const id = trimId(raw?.id);
  if (!RECORD_ITEM_KINDS.includes(itemKind) || !id) return null;
  return {
    itemKind,
    id,
    label: trimId(raw?.label),
    address: trimId(raw?.address),
    matched: raw?.matched !== false,
    // Seleccionable = tiene consultas/leads. Sin el campo (API previa) → `matched`.
    hasActivity: typeof raw?.has_activity === 'boolean' ? raw.has_activity : raw?.matched !== false,
    counts: readRecordCounts(raw?.counts),
    // M3 (`include=lists`): listas del owner donde está el registro; CAD siempre [].
    lists: (Array.isArray(raw?.lists) ? raw.lists : [])
      .map((l) => ({ id: trimId(l?.id), name: trimId(l?.name) }))
      .filter((l) => l.id && l.name),
  };
}

/**
 * @param {object} res Envelope de GET /searchleadrecords.
 * @returns {{ groups: { itemKind: string, id: string, label: string, address: string,
 *   matched: boolean, hasActivity: boolean, counts: object, units: object[] }[],
 *   hasMore: boolean, nextOffset: number|null, totalFiltered: number|null, truncated: boolean }}
 */
export function readLeadRecordSearch(res) {
  const rows = Array.isArray(res?.data) ? res.data : [];
  const groups = rows.map((row) => {
    const group = readRecordHit(row);
    if (!group) return null;
    const units = (Array.isArray(row.units) ? row.units : []).map(readRecordHit).filter(Boolean);
    return { ...group, units };
  }).filter(Boolean);
  const numOrNull = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Math.trunc(Number(v)));
  const nextOffset = numOrNull(res?.next_offset);
  return {
    groups,
    hasMore: res?.has_more === true && nextOffset !== null,
    nextOffset,
    totalFiltered: numOrNull(res?.total_filtered),
    truncated: res?.truncated === true,
  };
}
